// PipelineRunStatus is a FIXED numeric enum shipped by Wave 1/2 (BE) — the JSON wire
// value is the raw integer 1-5 (no JsonStringEnumConverter is registered). AC-16 requires
// the FE to key on these numeric values explicitly, never by array index/ordinal position
// and never by inventing string labels the BE doesn't send.
export const PipelineRunStatus = {
  Queued: 1,
  Building: 2,
  Deploying: 3,
  Succeeded: 4,
  Failed: 5,
  // deploy-pipelines-run-cancel (Wave 5, AC-1/AC-22) — a sixth, TERMINAL status, distinct
  // from Failed: a deliberate cancel is not a failure (Q2). Explicit value, like every
  // member above — never a C# ordinal.
  Cancelled: 6,
};

// Explicit numeric-key lookup (not an array) so a future enum member added out of order
// can never shift every other label.
export const PIPELINE_RUN_STATUS_LABEL = {
  [PipelineRunStatus.Queued]: 'Queued',
  [PipelineRunStatus.Building]: 'Building',
  [PipelineRunStatus.Deploying]: 'Deploying',
  [PipelineRunStatus.Succeeded]: 'Succeeded',
  [PipelineRunStatus.Failed]: 'Failed',
  [PipelineRunStatus.Cancelled]: 'Cancelled',
};

// MUI Chip `color` prop values — house palette already meets WCAG 2.1 AA contrast for
// these semantic colors (used the same way for AppInstance/App status chips elsewhere
// in this codebase), so no bespoke hex is introduced here (NFR-Accessibility).
//
// deploy-pipelines-run-cancel (Wave 5, AC-22) — Cancelled is deliberately neither 'error'
// (a deliberate stop is not a failure) nor 'success'. 'secondary' is the one MUI semantic
// Chip colour not already claimed by another status in this map, so a cancelled run's chip
// reads as visually distinct from every shipped status, not merely from Succeeded/Failed.
export const PIPELINE_RUN_STATUS_CHIP_COLOR = {
  [PipelineRunStatus.Queued]: 'default',
  [PipelineRunStatus.Building]: 'info',
  [PipelineRunStatus.Deploying]: 'warning',
  [PipelineRunStatus.Succeeded]: 'success',
  [PipelineRunStatus.Failed]: 'error',
  [PipelineRunStatus.Cancelled]: 'secondary',
};

export const TERMINAL_PIPELINE_RUN_STATUSES = [
  PipelineRunStatus.Succeeded,
  PipelineRunStatus.Failed,
  PipelineRunStatus.Cancelled,
];

export const isTerminalPipelineRunStatus = (status) =>
  TERMINAL_PIPELINE_RUN_STATUSES.includes(status);

// ---------------------------------------------------------------------------
// pipelines-list-sort-and-narrow (AC-7/AC-8) — the Latest Run column's SORT ORDER.
// ---------------------------------------------------------------------------
//
// The list is a WORKLIST, so the Latest Run column sorts by "does this row need a human?",
// most-actionable FIRST in the ascending direction. That direction is named here, explicitly,
// because AC-7 requires the intent to be checkable by a reviewer and not merely inferable from
// the output.
//
// WHY NOT the raw numeric enum value (AC-8 forbids it): the wire values happen to run
// in-flight 1-3 then terminal 4-6 today, so raw-numeric sorting looks right BY COINCIDENCE. A
// seventh member appended as 7 would sort after Succeeded whatever it meant. The coincidence is
// not a contract, so it is not relied on.
//
// WHY NOT alphabetical (AC-8 forbids it): Building/Cancelled/Deploying/Failed/Queued/Succeeded
// interleaves in-flight and terminal states into an order with no operational meaning at all.
//
// The declared order, ascending:
//   0 never-run  — nothing has ever been built here. The MOST actionable row on the page, and
//                  the reason AC-7 puts it first rather than treating null as "missing data".
//   1 Failed     — needs a fix and a re-run: the most actionable thing that HAS run.
//   2 Cancelled  — someone deliberately stopped it (Wave 5). Ranked next to Failed because it
//                  probably wants re-running, but DISTINCTLY from it, because a cancel is not a
//                  failure — AC-8 requires the two not be folded together.
//   3 Queued     ┐
//   4 Building   ├ in flight: nothing to do but wait, kept in LIFECYCLE order so a reader can
//   5 Deploying  ┘ see how far along each one is.
//   6 Succeeded  — done, nothing to do. Least actionable, therefore last.
//
// Descending is the exact reverse: MUI negates the comparator, so "quiet rows first" comes free
// and no direction-aware branching is needed.
export const NEVER_RUN_PIPELINE_STATUS_RANK = 0;

export const PIPELINE_RUN_STATUS_RANK = {
  [PipelineRunStatus.Failed]: 1,
  [PipelineRunStatus.Cancelled]: 2,
  [PipelineRunStatus.Queued]: 3,
  [PipelineRunStatus.Building]: 4,
  [PipelineRunStatus.Deploying]: 5,
  [PipelineRunStatus.Succeeded]: 6,
};

// A null/undefined status is the never-run bucket — a real, first-class value on this page, not
// absent data.
//
// An UNKNOWN status (a seventh BE member this FE has not been taught) deliberately falls to
// Number.MAX_SAFE_INTEGER rather than 0: it sorts to the far end instead of impersonating the
// never-run bucket at the top of the actionable list. The real guard is the test that asserts
// every PipelineRunStatus member has a rank (AC-8) — that fails loudly at build time, which is
// the point. This is only the runtime fallback behind it.
export const getPipelineRunStatusRank = (status) => {
  if (status == null) return NEVER_RUN_PIPELINE_STATUS_RANK;
  return PIPELINE_RUN_STATUS_RANK[status] ?? Number.MAX_SAFE_INTEGER;
};

// deploy-pipelines-run-cancel (Wave 5, AC-17) — the explicit ALLOW-list the Cancel action is
// gated on. An allow-list (rather than "not disabled/not terminal") means a FUTURE
// PipelineRunStatus member is absent-by-default — nobody has to remember to exclude it, the
// opposite of a negated-terminal check which would silently opt every new member IN.
export const CANCELLABLE_PIPELINE_RUN_STATUSES = [
  PipelineRunStatus.Queued,
  PipelineRunStatus.Building,
];

export const isCancellablePipelineRunStatus = (status) =>
  CANCELLABLE_PIPELINE_RUN_STATUSES.includes(status);

// OQ-7 default: 3-5s poll interval.
export const PIPELINE_RUN_POLL_INTERVAL_MS = 4000;

// deploy-pipelines-run-logs OQ-2 — the BUILD LOG's own cadence. A separate, slower, log-only
// timer; it is NOT a second run-status poller (Wave 1 AC-17 still holds: PipelineRunStatusView
// owns the one and only usePollPipelineRun on this page).
//
// WHY 30s AND NOT 4s: the run poller's 4s tick is the wrong cadence for this data. The log
// column is refreshed by DockerRepositoryHostedService, whose period is the server setting
// `TimerCheckDockerRepoStatusIntervalInSeconds` (default 120). Polling the log at 4s would
// issue ~30 requests per actual change, every one of them returning identical bytes.
//
// WHY 30s AND NOT 120s: that 120 is SERVER CONFIG THE BROWSER CANNOT READ, and an operator is
// free to lower it. Matching a number we cannot observe would silently go stale the moment
// someone edited it. 30s is the defensible choice without that knowledge: it bounds staleness
// to ~30s past a write-back whatever the server is set to, at 1/8th the run poller's request
// rate (2 log requests per minute against its 15).
//
// This timer runs ONLY while Status === Building. Once the run is in any other status the log
// is already final — the same ExecuteUpdateAsync that makes the build terminal writes the last
// of the log — so the hook issues ZERO further requests rather than a slower poll.
export const PIPELINE_RUN_LOG_POLL_INTERVAL_MS = 30000;
